import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MathLatexComponent } from '../latex/math-latex/math-latex.component';

interface QuestionBlock {
  type: 'text' | 'latex' | 'image' | 'table';
  content?: string;
  s3_key?: string;
  alt_text?: string;
  rows?: string[][];
}

/**
 * Renders a question or option from its blocks array.
 * Falls back to the flat content string (rendered via MathLatexComponent)
 * for questions that predate the blocks field.
 */
@Component({
  selector: 'app-question-blocks',
  standalone: true,
  imports: [CommonModule, MathLatexComponent],
  templateUrl: './question-blocks.component.html',
  styleUrls: ['./question-blocks.component.css']
})
export class QuestionBlocksComponent {
  /** Structured blocks — present on questions extracted after the pipeline upgrade.
   *  Typed as any[] to accept the looser model-interface shapes across components. */
  @Input() blocks: any[] | null | undefined = null;

  /** Flat text fallback — question_text or option.text (always present). */
  @Input() content: string = '';

  /** Diagrams array for fallback path — resolves [DIAGRAM_N] in flat string. */
  @Input() diagrams: any[] = [];

  @Input() stripOptionPrefix: boolean = false;

  get useBlocks(): boolean {
    return Array.isArray(this.blocks) && (this.blocks as any[]).length > 0;
  }

  get hasImageBlock(): boolean {
    return !!this.blocks?.some(block => block.type === 'image');
  }

  get processedBlocks(): any[] {
    if (!this.blocks) return [];
    if (!this.stripOptionPrefix) return this.blocks;

    return this.blocks.map((block, index) => {
      if (index === 0 && block.content) {
        const cleanedContent = String(block.content)
          .replace(/^\s*[A-D][.)]\s+/, '')
          .replace(/^\s*\([A-D]\)\s+/, '')
          .replace(/^\s*\(\d+\)\s+/, '')
          .replace(/^\s*\d+[.)]\s+/, '')
          .trim();
        return {
          ...block,
          content: cleanedContent
        };
      }

      return block;
    });
  }

  isMathText(content: string): boolean {
    if (!content) return false;

    return /\\[a-zA-Z]+/.test(content)
      || /\$.*?\$/.test(content)
      || /\^/.test(content)
      || /_/.test(content)
      || /\\frac/.test(content);
  }
}
